// src/pages/DonorManagement.js
import { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DonorList from "../components/Donor/DonorList";
import DonorFormModal from "../components/Donor/DonorFormModal";
import DonationFormModal from "../components/Donation/DonationFormModal";
import { usePageLogger } from "../hooks/usePageLogger";
import { logger } from "../utils/logger";
import { fetchDropdownOptions } from "../redux/dropdownoptionsSlice";
import SnackbarAlert from "../components/SnackBarAlert";
import * as donorHandlers from "../components/Donor/utils/donorHandlers";

const DonorManagement = () => {
  const donor = useSelector((state) => state.donor.donors);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const formRef = useRef();
  const [isDonorFormOpen, setIsDonorFormOpen] = useState(false);
  const [isDonationFormOpen, setIsDonationFormOpen] = useState(false);
  const [isExistingDonorOpen, setIsExistingDonorOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [openConfirm, setOpenConfirm] = useState(false);
  const { data = {} } = useSelector((state) => state.dropdownOptions);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  useEffect(() => {
    if (!data.salutation.length) {
      dispatch(fetchDropdownOptions("salutation"));
      logger.info("Dropdown options for salutation fetched");
    }
    if (!data.status.length) {
      dispatch(fetchDropdownOptions("status"));
      logger.info("Dropdown options for status fetched");
    }
  }, [dispatch, data.salutation.length, data.status.length]);

  usePageLogger("Donors Page", { username: user?.username, role: user?.role });

  // Open modal to view/edit existing donor
  const openDonorFormCallback = useCallback((donor) => {
    donorHandlers.openDonorForm(donor, user, setEditData, setIsExistingDonorOpen);
  }, [user]);

  // Open modal to add a new donor
  const handleAddDonorCallback = useCallback(() => {
    donorHandlers.handleAddDonor(user, setEditData, setIsDonorFormOpen);
  }, [user]);

  const handleCloseModalCallback = useCallback(() => {
    donorHandlers.handleCloseModal(setIsExistingDonorOpen);
  }, []);

  const handleCloseFeedbackCallback = useCallback(() => {
    donorHandlers.handleCloseFeedback(feedback, setFeedback);
  }, [feedback]);

  // Open modal to edit an existing donor
  const handleEditDonorCallback = useCallback((donor) => {
    donorHandlers.handleEditDonor(
      donor,
      user,
      setEditData,
      setIsDonorFormOpen,
      setIsDonationFormOpen
    );
  }, [user]);

  // Save donor data (create or update)
  const handleSaveDonorCallback = useCallback(
    async (data) => {
      await donorHandlers.handleSaveDonor(
        data,
        editData,
        isExistingDonorOpen,
        dispatch,
        user,
        setFeedback,
        setEditData,
        setIsDonorFormOpen,
        setIsDonationFormOpen,
        setOpenConfirm
      );
    },
    [dispatch, editData, isExistingDonorOpen, user]
  );

  // Open donation modal from donor modal
  const handleAddDonationCallback = useCallback((data) => {
    donorHandlers.handleAddDonation(
      data,
      user,
      setIsDonorFormOpen,
      setIsExistingDonorOpen,
      setIsDonationFormOpen
    );
  }, [user]);

  // Save donation data (create or update)
  const handleSaveDonationCallback = useCallback(
    async (data) => {
      await donorHandlers.handleSaveDonation(
        data,
        editData,
        dispatch,
        user,
        setFeedback,
        setIsDonorFormOpen,
        setIsDonationFormOpen
      );
    },
    [dispatch, editData, user]
  );

  return (
    <Box
      sx={{
        padding: 3,
        flexGrow: 1,
        transition: "margin-left 0.3s",
        overflowX: "hidden",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Donor Management
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button variant="contained" color="primary" onClick={handleAddDonorCallback}>
          Add New Donor
        </Button>
      </Box>

      <DonorFormModal
        open={isExistingDonorOpen}
        onClose={handleCloseModalCallback}
        onSubmit={handleSaveDonorCallback}
        editData={editData}
        formRef={formRef}
        dropdownData={data}
        addDonation={true}
        onDonationSubmit={handleAddDonationCallback}
        userRole={user?.role}
      />

      <DonorList onEdit={handleEditDonorCallback} openDonorForm={openDonorFormCallback} />
      <DonorFormModal
        open={isDonorFormOpen}
        onClose={() => setIsDonorFormOpen(false)}
        onSubmit={handleSaveDonorCallback}
        editData={editData}
        formRef={formRef}
        dropdownData={data}
        addDonation={true}
        onDonationSubmit={handleAddDonationCallback}
        userRole={user?.role}
      />
      <DonationFormModal
        open={isDonationFormOpen}
        onClose={() => setIsDonationFormOpen(false)}
        onSubmit={handleSaveDonationCallback}
        editData={editData}
        formRef={formRef}
      />

      <SnackbarAlert feedback={feedback} onClose={handleCloseFeedbackCallback} />
    </Box>
  );
};

export default DonorManagement;
